import { Check, ArrowRight, Mail, Phone } from "lucide-react";
import { LocaleLink } from "../i18n/LocaleLink";
import { Container, Eyebrow, ImgPlaceholder } from "../components/ui";
import { PageHeading, SectionHeading, Button } from "../ds/core";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { TRIAL_PATH } from "../config/entrypoints";

export default function About() {
  const { t } = useLocale();
  return (
    <>
      <PageHeading
        tone="light"
        eyebrow={t("about:hero.eyebrow")}
        title={t("about:hero.title")}
        intro={t("about:hero.intro")}
      />

      {/* Mission */}
      <section className="bg-bg">
        <Container className="grid gap-12 py-16 md:grid-cols-[1fr_1.1fr] md:items-center">
          <div>
            <Eyebrow>{t("about:mission.eyebrow")}</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-semibold leading-snug tracking-tight text-ink md:text-[30px]">
              {t("about:mission.title")}
            </h2>
          </div>
          <p className="text-[17px] leading-relaxed text-ink-soft">
            {t("about:mission.quote")}
          </p>
        </Container>
      </section>

      {/* Hero photo */}
      <section className="bg-bg">
        <Container className="py-8">
          <ImgPlaceholder label={t("about:photo.label")} ratio="aspect-[21/9]" src={cmsImage(t, "about:photo.image", "")} />
        </Container>
      </section>

      {/* Origin story */}
      <section className="bg-bg-subtle">
        <Container className="grid gap-12 py-16 md:grid-cols-2">
          <div>
            <SectionHeading eyebrow={t("about:origin.eyebrow")} title={t("about:origin.title")} />
            <p className="mt-5 text-[16px] leading-relaxed text-ink-soft">
              {t("about:origin.p1")}
            </p>
            <p className="mt-4 text-[16px] leading-relaxed text-ink-soft">
              {t("about:origin.p2")}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            {[
              { t: t("about:value.simple.title"), d: t("about:value.simple.body") },
              { t: t("about:value.noHardware.title"), d: t("about:value.noHardware.body") },
              { t: t("about:value.affordable.title"), d: t("about:value.affordable.body") },
              { t: t("about:value.legal.title"), d: t("about:value.legal.body") },
            ].map((v) => (
              <div key={v.t} className="flex items-start gap-4 rounded-2xl border border-line bg-white p-5">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success-soft text-success">
                  <Check size={16} strokeWidth={2.4} />
                </span>
                <div>
                  <h4 className="font-display text-[16px] font-semibold text-ink">{v.t}</h4>
                  <p className="mt-1 text-[14px] leading-relaxed text-ink-soft">{v.d}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Team & clients */}
      <section className="bg-bg">
        <Container className="grid gap-8 py-16 md:grid-cols-2">
          <div className="rounded-2xl border border-line bg-bg-subtle p-4 md:p-7">
            <Eyebrow>{t("about:team.eyebrow")}</Eyebrow>
            <h3 className="mt-3 font-display text-[22px] font-semibold text-ink">{t("about:team.title")}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">
              {t("about:team.p1")}
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">
              {t("about:team.p2")}
            </p>
          </div>
          <div className="rounded-2xl border border-line bg-bg-subtle p-4 md:p-7">
            <Eyebrow>{t("about:clients.eyebrow")}</Eyebrow>
            <h3 className="mt-3 font-display text-[22px] font-semibold text-ink">{t("about:clients.title")}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">
              {t("about:clients.body")}
            </p>
            <LocaleLink to="/industries" className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline">
              {t("about:clients.cta")} <ArrowRight size={13} strokeWidth={2} />
            </LocaleLink>
          </div>
        </Container>
      </section>

      {/* Contact */}
      <section className="bg-bg-subtle">
        <Container className="py-14">
          <div className="grid gap-8 md:grid-cols-[1fr_auto]">
            <div>
              <Eyebrow>{t("about:contact.eyebrow")}</Eyebrow>
              <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-ink md:text-[28px]">
                {t("about:contact.title")}
              </h2>
              <p className="mt-3 max-w-md text-[15px] leading-relaxed text-ink-soft">
                {t("about:contact.body")}
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <a href="mailto:info@done-it.app" className="inline-flex items-center gap-2 text-[15px] font-medium text-ink hover:text-primary">
                  <Mail size={16} strokeWidth={2} className="text-ink-faint" /> info@done-it.app
                </a>
                <a href="tel:+32473735794" className="inline-flex items-center gap-2 text-[15px] font-medium text-ink hover:text-primary">
                  <Phone size={16} strokeWidth={2} className="text-ink-faint" /> +32 473 73 57 94
                </a>
              </div>
              <p className="mt-5 text-[12px] text-ink-faint">{t("about:contact.entity")}</p>
            </div>
            <div className="flex flex-col justify-center gap-3">
              <Button to={TRIAL_PATH} variant="solid">{t("about:contact.startTrial")}</Button>
              <Button to="/contact" variant="outline">{t("about:contact.sendMessage")}</Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

